import React, { useContext, useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import Swal from "sweetalert2";
import { DarkModeContext } from "../../context/DarkModeContext";
import logo from "../../assets/img/logo.png";
import { getToken } from "../../utils/auth";


const ModalEditarNroComercio = ({ show, onHide, comercio, onSuccess }) => {
    const { darkMode } = useContext(DarkModeContext);
    const [formData, setFormData] = useState({
        nroComercio1: "",
        nomRubro: "",
        categoria: "",
        legajos: "",
    });
    const [categorias, setCategorias] = useState([]);
    const [legajos, setLegajos] = useState([]);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        setFormData({
            nroComercio1: comercio?.nroComercio1?.toString() || "",
            nomRubro: comercio?.nomRubro || "",
            categoria: comercio?.categoria || "",
            legajos: comercio?.legajos ?? "",
        });
    }, [comercio, show]);

    useEffect(() => {
        if (!show) {
            return;
        }

        const cargarOpciones = async () => {
            try {
                const [categoriasResponse, legajosResponse] = await Promise.all([
                    fetch(process.env.REACT_APP_API_RUBROS),
                    fetch(process.env.REACT_APP_API_LEGAJO_LISTA_COMPLETO),
                ]);

                if (categoriasResponse.ok) {
                    const categoriasData = await categoriasResponse.json();
                    setCategorias(
                        (Array.isArray(categoriasData) ? categoriasData : []).map((categoria) => ({
                            value: categoria,
                            label: categoria,
                        }))
                    );
                }

                if (legajosResponse.ok) {
                    const legajosData = await legajosResponse.json();
                    setLegajos(
                        (Array.isArray(legajosData) ? legajosData : []).map((legajo) => ({
                            value: legajo.id,
                            label: `${legajo.nroLegajo ?? ""} - ${legajo.terminal ?? ""} - ${legajo.nombreDeFantasia ?? ""}`,
                        }))
                    );
                }
            } catch (error) {
                console.error("No se pudieron cargar las opciones para editar comercio.", error);
            }
        };

        cargarOpciones();
    }, [show]);

    const setCampo = (campo, valor) => {
        setFormData((prev) => ({ ...prev, [campo]: valor }));
    };

    const mostrarMensaje = (title, text, icon) => {
        Swal.fire({
            title,
            text,
            icon,
            imageUrl: icon === "success" ? undefined : logo,
            imageWidth: 100,
            imageHeight: 30,
            imageAlt: "Logo",
            confirmButtonText: "Ok",
            allowOutsideClick: true,
            customClass: {
                container: darkMode ? "swal2-container--dark" : "swal2-container--light",
                confirmButton: "my-swal-button",
            },
        });
    };

    const guardar = async (e) => {
        e.preventDefault();

        const token = getToken();
        if (!token) {
            mostrarMensaje("Error", "Token no disponible.", "error");
            return;
        }

        if (!formData.nroComercio1.trim() || !formData.nomRubro.trim() || !formData.categoria.trim() || !formData.legajos) {
            mostrarMensaje("Error", "Todos los campos son obligatorios.", "error");
            return;
        }

        setGuardando(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_COMERCIO_EDITAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    Id: comercio.id,
                    NroComercio1: formData.nroComercio1.trim(),
                    NomRubro: formData.nomRubro.trim(),
                    Categoria: formData.categoria.trim(),
                    Legajos: Number(formData.legajos),
                }),
            });

            const text = await response.text();
            if (!response.ok) {
                throw new Error(text || "No se pudo editar el comercio.");
            }

            mostrarMensaje("Exito", "El comercio se edito correctamente.", "success");
            onSuccess?.();
            onHide?.();
        } catch (error) {
            mostrarMensaje("Error", error.message || "No se pudo editar el comercio.", "error");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" aria-labelledby="modal-editar-comercio">
            <Modal.Body className={darkMode ? "modal-content text-black" : "modal-content text-black"}>
                <section className="d-flex justify-content-between my-4">
                    <div className="ocultar-div"></div>
                    <div className="d-flex justify-content-center">
                        <h6 className="fs-18 lato-bold">Editar Comercio</h6>
                    </div>
                    <div>
                        <button className="border-0 btn-filtro-cruz" onClick={onHide}>
                            <FontAwesomeIcon className="fs-18" icon={faXmark} />
                        </button>
                    </div>
                </section>

                <form onSubmit={guardar}>
                    <div className="form-group col-12">
                        <label className="fs-14 mb-2 ms-1">Nro de Comercio</label>
                        <input
                            type="text"
                            className="form-control input-cargar-nueva-busqueda"
                            value={formData.nroComercio1}
                            onChange={(e) => setCampo("nroComercio1", e.target.value)}
                            maxLength={8}
                        />
                    </div>

                    <div className="form-group col-12 mt-3">
                        <label className="fs-14 mb-2 ms-1">Rubro</label>
                        <input
                            type="text"
                            className="form-control input-cargar-nueva-busqueda"
                            value={formData.nomRubro}
                            onChange={(e) => setCampo("nomRubro", e.target.value)}
                        />
                    </div>

                    <div className="form-group col-12 mt-3">
                        <label className="fs-14 mb-2 ms-1">Categoria</label>
                        <Select
                            options={categorias}
                            className="select-control-custom lato-bold border-0"
                            classNamePrefix="select"
                            placeholder="Seleccione Categoria"
                            onChange={(selectedOption) =>
                                setCampo("categoria", selectedOption?.value || "")
                            }
                            value={
                                categorias.find((option) => option.value === formData.categoria) || null
                            }
                        />
                    </div>

                    <div className="form-group col-12 mt-3">
                        <label className="fs-14 mb-2 ms-1">Legajo</label>
                        <Select
                            options={legajos}
                            className="select-control-custom lato-bold border-0"
                            classNamePrefix="select"
                            placeholder="Seleccione Legajo"
                            onChange={(selectedOption) =>
                                setCampo("legajos", selectedOption?.value || "")
                            }
                            value={
                                legajos.find((option) => option.value === formData.legajos) || null
                            }
                        />
                    </div>

                    <div className="d-flex justify-content-between mt-5">
                        <button
                            type="button"
                            className="btn-cancel-modificaciones-nuevo-puesto border-0 lato-bold text-white"
                            onClick={onHide}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-guardar-modificaciones-nuevo-puesto border-0 lato-bold text-white"
                            disabled={guardando}
                        >
                            {guardando ? <Spinner animation="border" size="sm" /> : "Guardar"}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default ModalEditarNroComercio;
